import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import {
  getTargetCurrency,
  normalizeWalletCurrency,
  type WithdrawalMode,
  type WalletCurrency,
} from '../utils/withdrawalConversion';

interface WithdrawalCurrencyModeProps {
  walletCurrency: string;
  mode: WithdrawalMode;
  onModeChange: (mode: WithdrawalMode) => void;
  convertEnabled?: boolean;
  className?: string;
}

export default function WithdrawalCurrencyMode({
  walletCurrency,
  mode,
  onModeChange,
  convertEnabled = true,
  className,
}: WithdrawalCurrencyModeProps) {
  const { t } = useTranslation('wallet');
  const wallet = normalizeWalletCurrency(walletCurrency);
  const target = getTargetCurrency(wallet);

  const currencyLabel = (currency: WalletCurrency) =>
    t(`withdrawal_mode.currency_${currency}`);

  const options: { value: WithdrawalMode; title: string; description: string }[] = [
    {
      value: 'original',
      title: t('withdrawal_mode.original_title', { currency: currencyLabel(wallet) }),
      description: t('withdrawal_mode.original_description'),
    },
    ...(convertEnabled
      ? [
          {
            value: 'convert' as const,
            title: t('withdrawal_mode.convert_title', { currency: currencyLabel(target) }),
            description: t('withdrawal_mode.convert_description', {
              from: wallet,
              to: target,
            }),
          },
        ]
      : []),
  ];

  return (
    <div
      className={cn('space-y-2', className)}
      role="radiogroup"
      aria-label={t('withdrawal_mode.question')}
    >
      <p className="text-sm text-zinc-400">{t('withdrawal_mode.question')}</p>
      <div className="grid gap-2 md:grid-cols-2">
        {options.map((option) => {
          const isSelected = mode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              data-cy={`modo-saque-${option.value}`}
              onClick={() => onModeChange(option.value)}
              className={cn(
                'w-full rounded-lg border p-3 text-left transition-colors',
                isSelected
                  ? 'border-primary/60 bg-primary/10'
                  : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700',
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                    isSelected ? 'border-primary' : 'border-zinc-600',
                  )}
                >
                  {isSelected && <span className="h-2 w-2 rounded-full bg-primary" />}
                </span>
                <span className="min-w-0">
                  <span
                    className={cn(
                      'block text-sm font-medium',
                      isSelected ? 'text-primary' : 'text-zinc-200',
                    )}
                  >
                    {option.title}
                  </span>
                  <span className="block text-xs text-zinc-500 mt-0.5 leading-relaxed">
                    {option.description}
                  </span>
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
